import { ReactNode, useState } from 'react'

import AdjustmentsIcon from '@heroicons/react/outline/AdjustmentsIcon'
import CogIcon from '@heroicons/react/outline/CogIcon'
import LogoutIcon from '@heroicons/react/outline/LogoutIcon'
import ShieldCheckIcon from '@heroicons/react/outline/ShieldCheckIcon'
import SupportIcon from '@heroicons/react/outline/SupportIcon'
import TemplateIcon from '@heroicons/react/outline/TemplateIcon'
import UserCircleIcon from '@heroicons/react/outline/UserCircleIcon'
import { useDecision } from '@optimizely/react-sdk'
import { Link, useHistory } from 'react-router-dom'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import {
  SystemSpaceSlug,
  useAuthToken,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { alert } from '@tribeplatform/react-ui-kit/Dialog'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'

import { useLogout } from '../../../hooks/useLogout'
import { FeaturesList } from '../../../lib/optimizely/feature.list'
import { SupportModal } from '../../SupportModal'
import { NavbarControlItem } from '../constants'

type ControlProfileItem = {
  name?: string
  href?: string
  key?: string
  icon?: ReactNode
  as?: 'a' | Link
  action?: () => void
}

export type NavbarControlProfileProps = {
  item: NavbarControlItem
  presentation: ReactNode
}

export const NavbarControlProfile = ({
  item,
  presentation,
}: NavbarControlProfileProps) => {
  const {
    data: { network },
  } = useAuthToken()

  const [decisionAdmin] = useDecision(FeaturesList.NewAdminPanel)
  const [decisionModeration] = useDecision(FeaturesList.NewModerationPanel)

  const { logout } = useLogout()
  const [supportModal, setSupportModal] = useState<boolean>(false)
  const { generatePath, generateProfilePath } = useSpaceUtil()

  const [canUpdateNetwork] = hasScopesPermission(network, ['updateNetwork'])

  const history = useHistory()
  const accountSettingsPath = generatePath(SystemSpaceSlug.PROFILE_SETTINGS)

  const openCustomizer = () => {
    if (window.innerWidth < 850) {
      alert({
        title: 'Screen Size Too Small',
        description:
          'We currently do not support using the customizer on small-screen devices. If you are using a tablet, try rotating it to the landscape orientation, or try a device with a larger screen.',
      })
    } else {
      history.push('/customizer')
    }
  }

  let profileItems: Array<ControlProfileItem> = [
    {
      name: 'Your Profile',
      href: generateProfilePath(),
      icon: <UserCircleIcon />,
      key: 'your-profile',
    },
    {
      name: 'Account Settings',
      href: `${accountSettingsPath}/account`,
      icon: <AdjustmentsIcon />,
      key: 'account-settings',
    },
  ]
  if (canUpdateNetwork) {
    if (decisionAdmin.enabled || decisionModeration.enabled) {
      profileItems.push({ name: '-' })
      if (decisionAdmin.enabled) {
        profileItems.push({
          name: (
            <div className="flex items-center">
              Administration
              <Badge className="mx-1" variant="secondary" rounded size="sm">
                Beta
              </Badge>
            </div>
          ),
          href: '/manage/people/members',
          icon: <CogIcon />,
          key: 'customizer',
        })
      }
      if (decisionModeration.enabled) {
        profileItems.push({
          name: (
            <div className="flex items-center">
              Moderation
              <Badge className="mx-1" variant="secondary" rounded size="sm">
                Beta
              </Badge>
            </div>
          ),
          href: '/settings/moderation',
          icon: <ShieldCheckIcon />,
          key: 'customizer',
        })
      }
    }

    profileItems = profileItems.concat([
      { name: '-' },
      {
        name: 'Administration',
        as: 'a',
        href: '/admin/network/settings',
        icon: <CogIcon />,
        key: 'administration',
      },
      {
        name: 'Customizer',
        action: openCustomizer,
        icon: <TemplateIcon />,
        key: 'customizer',
      },
      {
        name: 'Help & Community',
        action: () => setSupportModal(true),
        icon: <SupportIcon />,
        key: 'help',
      },
    ])
  }
  profileItems = profileItems.concat([
    { name: '-' },
    {
      name: 'Sign out',
      action: () => logout(),
      icon: <LogoutIcon />,
      key: 'sign-out',
    },
  ])

  return (
    <>
      <Dropdown placement="bottom-end">
        <Dropdown.ButtonMinimal
          className="navbar-profile-menu"
          aria-label={item.label}
        >
          {presentation}
        </Dropdown.ButtonMinimal>
        <Dropdown.Items className="navbar-profile-menu__dropdown">
          {profileItems.map((item, index) => {
            if (item?.name === '-')
              return (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`static-key-navbar-divider-${index}`}
                  className="w-full border-t border-surface-200/50 my-2 menu-divider"
                />
              )

            if (item.as === 'a')
              return (
                <Dropdown.Item
                  leadingIcon={item.icon}
                  key={item.name}
                  as="a"
                  href={item.href || '#'}
                  onClick={item.action}
                  data-id={item.key}
                >
                  {item.name}
                </Dropdown.Item>
              )

            return (
              <Dropdown.Item
                leadingIcon={item.icon}
                key={item.name}
                as={Link}
                to={item.href || '#'}
                onClick={item.action}
                data-id={item.key}
              >
                {item.name}
              </Dropdown.Item>
            )
          })}
        </Dropdown.Items>
      </Dropdown>
      <SupportModal
        open={supportModal}
        onClose={() => setSupportModal(false)}
      />
    </>
  )
}
